import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppwriteClientService } from '@prism/angular-shared';
import { ClientDevice, User } from '@prism/prism-shared';
import { filter, startWith } from 'rxjs/operators';
import { buildInfo } from '../../build-info.const';
import { CoreSelectors } from '../state/core';
import { AppwriteAuthService, AppwriteAuthUser } from './appwrite-auth.service';

const PRISM_DIAGNOSTIC_HEADER = 'X-Requested-With';

@Injectable({ providedIn: 'root' })
export class PrismRequestDiagnosticsService {
    private readonly browserIdStorageKey = 'prismDiagnosticBrowserId';
    private initialized = false;

    private authUser: AppwriteAuthUser | null = null;
    private currentUser: User | null = null;
    private currentClientDevice: ClientDevice | null = null;
    private currentRoute = '';
    private diagnosticHeaderValue = '';

    constructor(
        private appwrite: AppwriteClientService,
        private auth: AppwriteAuthService,
        private router: Router,
        private store: Store,
    ) { }

    public init(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        this.currentRoute = this.routePath(this.router.url) ?? '';
        this.updateHeaders();

        this.auth.authState.subscribe(authUser => {
            this.authUser = authUser;
            this.updateHeaders();
        });

        this.store.select(CoreSelectors.getCurrentUser).pipe(
            startWith(null),
        ).subscribe(currentUser => {
            this.currentUser = currentUser as User | null;
            this.updateHeaders();
        });

        this.store.select(CoreSelectors.getCurrentClientDevice).pipe(
            startWith(null),
        ).subscribe(currentClientDevice => {
            this.currentClientDevice = currentClientDevice;
            this.updateHeaders();
        });

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            startWith(null),
        ).subscribe(() => {
            this.currentRoute = this.routePath(this.router.url) ?? '';
            this.updateHeaders();
        });
    }

    public getHeaders(): Record<string, string> {
        return this.diagnosticHeaderValue
            ? { [PRISM_DIAGNOSTIC_HEADER]: this.diagnosticHeaderValue }
            : {};
    }

    private updateHeaders(): void {
        const userName = this.currentUser
            ? `${this.currentUser.firstName ?? ''} ${this.currentUser.lastName ?? ''}`.trim()
            : this.authUser?.displayName ?? this.authUser?.name ?? '';

        const fields: Record<string, string | undefined> = {
            userId: this.sanitizeHeaderValue(this.currentUser?.id ?? this.authUser?.uid),
            userName: this.sanitizeHeaderValue(userName),
            browserId: this.sanitizeHeaderValue(this.getBrowserId()),
            clientDeviceId: this.sanitizeHeaderValue(this.currentClientDevice?.id),
            clientDeviceName: this.sanitizeHeaderValue(this.currentClientDevice?.name),
            route: this.sanitizeHeaderValue(this.currentRoute, 220),
            build: this.sanitizeHeaderValue(buildInfo.deploymentId || buildInfo.commitHash),
        };

        this.diagnosticHeaderValue = this.formatDiagnosticHeader(fields);
        this.applyAppwriteHeaders();
    }

    private applyAppwriteHeaders(): void {
        delete this.appwrite.client.headers[PRISM_DIAGNOSTIC_HEADER];

        if (this.diagnosticHeaderValue) {
            this.appwrite.client.headers[PRISM_DIAGNOSTIC_HEADER] = this.diagnosticHeaderValue;
        }
    }

    private formatDiagnosticHeader(fields: Record<string, string | undefined>): string {
        const parts = Object.entries(fields)
            .filter(([, value]) => !!value)
            .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`);

        if (!parts.length) {
            return '';
        }

        return `PrismDiagnostics ${parts.join(';')}`.slice(0, 900);
    }

    private getBrowserId(): string {
        if (typeof window === 'undefined' || !window.localStorage) {
            return 'unknown-browser';
        }

        const existing = window.localStorage.getItem(this.browserIdStorageKey);
        if (existing) {
            return existing;
        }

        const generated = this.generateBrowserId();
        window.localStorage.setItem(this.browserIdStorageKey, generated);
        return generated;
    }

    private generateBrowserId(): string {
        const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
        if (cryptoApi?.randomUUID) {
            return cryptoApi.randomUUID();
        }

        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    }

    private routePath(route: string): string | undefined {
        return route.split('?')[0]?.split('#')[0];
    }

    private sanitizeHeaderValue(value: unknown, maxLength = 160): string | undefined {
        if (value === null || value === undefined) {
            return undefined;
        }

        const text = String(value)
            .trim()
            .replace(/[\r\n\t]+/g, ' ')
            .normalize('NFKD')
            .replace(/[^\x20-\x7E]/g, '');

        if (!text) {
            return undefined;
        }

        return text.slice(0, maxLength);
    }
}
